/**
 * Structured artifact × pipeline-step matrix for Flow 3 Flow map UI.
 * Mirrors docs/flow3-artifact-pipeline-reference.md
 */

export const FLOW3_STEPS = [
  { id: 'concept_tree_v3', shortLabel: '0', label: 'Concept tree' },
  { id: 'knowledge_analysis_v3', shortLabel: '1', label: 'Knowledge analysis' },
  { id: 'course_structure_v3', shortLabel: '2', label: 'Course structure' },
  { id: 'slide_architecture_v3', shortLabel: '3', label: 'Slide architecture' },
  { id: 'curriculum_optimization_v3', shortLabel: '3.5', label: 'Optimize' },
  { id: 'artifact_specification_v3', shortLabel: '4', label: 'Artifact specs' },
  { id: 'artifact_generation_v3', shortLabel: '5A', label: 'AKOs' },
  { id: 'artifact_rendering_v3', shortLabel: '5B', label: 'ARP' },
  { id: 'artifact_edm_v3', shortLabel: '5C.1', label: 'EDM' },
  { id: 'artifact_authoring_v3', shortLabel: '5C.2', label: 'Authoring' },
  { id: 'artifact_studio_v3', shortLabel: '5C.3', label: 'Studio' },
  { id: 'artifact_layout_engine_v3', shortLabel: '5C.4', label: 'Layout' },
  { id: 'artifact_svg_renderer_v3', shortLabel: '5C.5', label: 'SVG' },
  { id: 'artifact_rrao_v3', shortLabel: '5C.6', label: 'RRAO' },
];

export const ARTIFACT_TYPES = [
  {
    id: 'explanation',
    label: 'Explanation',
    prefix: 'EXL_',
    featured: true,
  },
  {
    id: 'diagram',
    label: 'Diagram',
    prefix: 'DIA_',
    featured: true,
  },
  {
    id: 'algorithm',
    label: 'Algorithm Walkthrough',
    prefix: 'ALG_',
    featured: true,
  },
  {
    id: 'dry_run',
    label: 'Dry Run',
    prefix: 'DRY_',
    featured: true,
  },
  {
    id: 'code_example',
    label: 'Code Example',
    prefix: 'COD_',
    featured: true,
  },
  {
    id: 'example',
    label: 'Example',
    prefix: 'EXM_',
    featured: true,
  },
  {
    id: 'flowchart',
    label: 'Flowchart',
    prefix: 'FLW_',
    featured: false,
  },
  {
    id: 'table_comparison',
    label: 'Table Comparison',
    prefix: 'TBL_',
    featured: false,
  },
];

const F = (summary, outputShape, backendFiles) => ({ summary, outputShape, backendFiles });

const PLANNING_NO_ARTIFACTS = F(
  'No artifact objects yet — concept-level planning only.',
  'concepts[], learning_path',
  ['backend/learning_tutorial/concept_tree_v3.py'],
);

const PLANNING_SUGGESTED = (typeName) =>
  F(
    `Type "${typeName}" may appear in suggested_artifacts / artifact_scores.`,
    'concepts[].suggested_artifacts[]',
    ['backend/learning_tutorial/knowledge_analysis_v3.py'],
  );

const PLANNING_MODULE = F(
  'Concepts assigned to modules; artifact types not instantiated.',
  'modules[], learning_sequence',
  ['backend/learning_tutorial/course_structure_v3.py'],
);

const PLANNING_SLIDE = (typeName) =>
  F(
    `Slide metadata assigns "${typeName}" type to slides (no IDs).`,
    'slides[].artifact_types[]',
    ['backend/learning_tutorial/slide_architecture_v3.py'],
  );

const PLANNING_OPTIMIZE = F(
  'Redundancy repair; optimized slide + artifact type distribution.',
  'optimized_slides[]',
  ['backend/learning_tutorial/curriculum_optimization_v3.py'],
);

const SPEC = (typeName) =>
  F(
    `Per-slide spec: purpose, complexity, generation_instructions for ${typeName}.`,
    'artifact_specs[]',
    ['backend/learning_tutorial/artifact_specification_v3.py'],
  );

const AUTHORING = F(
  'EDM persisted to authoring DB; versioned snapshots.',
  'published_artifacts[]',
  ['backend/learning_tutorial/artifact_authoring_v3.py'],
);

const STUDIO = F(
  'Studio editor entry (URL, version). No semantic change.',
  'studio_entries[]',
  ['backend/learning_tutorial/artifact_studio_v3.py'],
);

const LAYOUT = F(
  'ELK computes node x/y; writes positioned coordinates.',
  'layout_entries[]',
  ['backend/learning_tutorial/layout_engine/elk_runner.py'],
);

const RRAO = F(
  'Packages SVG + regions + narration/animation anchors for runtime.',
  'rrao_entries[]',
  ['backend/learning_tutorial/artifact_rrao_v3.py', 'backend/learning_tutorial/runtime/rrao/'],
);

/** @type {Record<string, Record<string, { summary: string, outputShape: string, backendFiles: string[] }>>} */
export const ARTIFACT_STEP_MATRIX = {
  explanation: {
    concept_tree_v3: PLANNING_NO_ARTIFACTS,
    knowledge_analysis_v3: PLANNING_SUGGESTED('Explanation'),
    course_structure_v3: PLANNING_MODULE,
    slide_architecture_v3: PLANNING_SLIDE('Explanation'),
    curriculum_optimization_v3: PLANNING_OPTIMIZE,
    artifact_specification_v3: SPEC('Explanation'),
    artifact_generation_v3: F(
      'AKO created: definition, explanation_layers, vocabulary in visual_model.',
      'artifact_knowledge_objects[]',
      ['backend/learning_tutorial/artifact_generation_v3.py'],
    ),
    artifact_rendering_v3: F(
      'ARP: asset_type explanation; spatial components for layers.',
      'rendered_artifact_objects[]',
      ['backend/learning_tutorial/artifact_rendering_v3.py'],
    ),
    artifact_edm_v3: F(
      'EDM artifact_type CUSTOM; multi-node enrichment graph.',
      'editable_diagram_models[].edm',
      ['backend/learning_tutorial/artifact_edm_v3.py', 'backend/learning_tutorial/edm/services/converters/visual_components.py'],
    ),
    artifact_authoring_v3: AUTHORING,
    artifact_studio_v3: STUDIO,
    artifact_layout_engine_v3: LAYOUT,
    artifact_svg_renderer_v3: F(
      'Diagram enrichment → concept cards + explanation layers SVG.',
      'render_entries[] + GET .../render/svg',
      [
        'backend/learning_tutorial/rendering/edm_enrichment.py',
        'backend/learning_tutorial/rendering/svg/renderer.py',
        'backend/learning_tutorial/edm/services/converters/visual_components.py',
      ],
    ),
    artifact_rrao_v3: RRAO,
  },
  diagram: {
    concept_tree_v3: PLANNING_NO_ARTIFACTS,
    knowledge_analysis_v3: PLANNING_SUGGESTED('Diagram'),
    course_structure_v3: PLANNING_MODULE,
    slide_architecture_v3: PLANNING_SLIDE('Diagram'),
    curriculum_optimization_v3: PLANNING_OPTIMIZE,
    artifact_specification_v3: SPEC('Diagram'),
    artifact_generation_v3: F(
      'AKO: components[], relationships[] in visual_model.',
      'artifact_knowledge_objects[]',
      ['backend/learning_tutorial/artifact_generation_v3.py'],
    ),
    artifact_rendering_v3: F(
      'ARP: asset_type diagram; components + bounding boxes.',
      'rendered_artifact_objects[]',
      ['backend/learning_tutorial/artifact_rendering_v3.py'],
    ),
    artifact_edm_v3: F(
      'EDM artifact_type ARCHITECTURE_DIAGRAM; nodes + edges.',
      'editable_diagram_models[].edm',
      ['backend/learning_tutorial/artifact_edm_v3.py'],
    ),
    artifact_authoring_v3: AUTHORING,
    artifact_studio_v3: STUDIO,
    artifact_layout_engine_v3: LAYOUT,
    artifact_svg_renderer_v3: F(
      'Default node/edge diagram SVG (labeled nodes, routed edges).',
      'render_entries[]',
      [
        'backend/learning_tutorial/rendering/svg/renderer.py',
        'backend/learning_tutorial/rendering/svg/node_renderer.py',
        'backend/learning_tutorial/rendering/svg/edge_renderer.py',
      ],
    ),
    artifact_rrao_v3: RRAO,
  },
  algorithm: {
    concept_tree_v3: PLANNING_NO_ARTIFACTS,
    knowledge_analysis_v3: PLANNING_SUGGESTED('Algorithm Walkthrough'),
    course_structure_v3: PLANNING_MODULE,
    slide_architecture_v3: PLANNING_SLIDE('Algorithm Walkthrough'),
    curriculum_optimization_v3: PLANNING_OPTIMIZE,
    artifact_specification_v3: SPEC('Algorithm Walkthrough'),
    artifact_generation_v3: F(
      'AKO: step_sequence, goal, pseudo_code in visual_model.',
      'artifact_knowledge_objects[]',
      ['backend/learning_tutorial/artifact_generation_v3.py'],
    ),
    artifact_rendering_v3: F(
      'ARP: asset_type algorithm; step nodes as components.',
      'rendered_artifact_objects[]',
      ['backend/learning_tutorial/artifact_rendering_v3.py'],
    ),
    artifact_edm_v3: F(
      'EDM artifact_type FLOWCHART; step sequence nodes.',
      'editable_diagram_models[].edm',
      ['backend/learning_tutorial/artifact_edm_v3.py'],
    ),
    artifact_authoring_v3: AUTHORING,
    artifact_studio_v3: STUDIO,
    artifact_layout_engine_v3: LAYOUT,
    artifact_svg_renderer_v3: F(
      'Diagram path: step flowchart (no hub edges).',
      'render_entries[]',
      [
        'backend/learning_tutorial/rendering/svg/renderer.py',
        'backend/learning_tutorial/edm/services/converters/visual_components.py',
      ],
    ),
    artifact_rrao_v3: RRAO,
  },
  dry_run: {
    concept_tree_v3: PLANNING_NO_ARTIFACTS,
    knowledge_analysis_v3: PLANNING_SUGGESTED('Dry Run'),
    course_structure_v3: PLANNING_MODULE,
    slide_architecture_v3: PLANNING_SLIDE('Dry Run'),
    curriculum_optimization_v3: PLANNING_OPTIMIZE,
    artifact_specification_v3: SPEC('Dry Run'),
    artifact_generation_v3: F(
      'AKO: state_transition_table, iteration_states in visual_model.',
      'artifact_knowledge_objects[]',
      ['backend/learning_tutorial/artifact_generation_v3.py'],
    ),
    artifact_rendering_v3: F(
      'ARP: asset_type table; tabular components.',
      'rendered_artifact_objects[]',
      ['backend/learning_tutorial/artifact_rendering_v3.py'],
    ),
    artifact_edm_v3: F(
      'EDM artifact_type TABLE; table nodes from trace data.',
      'editable_diagram_models[].edm',
      ['backend/learning_tutorial/artifact_edm_v3.py'],
    ),
    artifact_authoring_v3: AUTHORING,
    artifact_studio_v3: STUDIO,
    artifact_layout_engine_v3: LAYOUT,
    artifact_svg_renderer_v3: F(
      'render_profile table_trace → tabular execution trace SVG.',
      'render_entries[] (render_profile: table_trace)',
      [
        'backend/learning_tutorial/rendering/svg/table_content.py',
        'backend/learning_tutorial/rendering/svg/table_renderer.py',
        'backend/learning_tutorial/rendering/edm_enrichment.py',
      ],
    ),
    artifact_rrao_v3: RRAO,
  },
  code_example: {
    concept_tree_v3: PLANNING_NO_ARTIFACTS,
    knowledge_analysis_v3: PLANNING_SUGGESTED('Code Example'),
    course_structure_v3: PLANNING_MODULE,
    slide_architecture_v3: PLANNING_SLIDE('Code Example'),
    curriculum_optimization_v3: PLANNING_OPTIMIZE,
    artifact_specification_v3: SPEC('Code Example'),
    artifact_generation_v3: F(
      'AKO: code, line_by_line_breakdown in visual_model.',
      'artifact_knowledge_objects[]',
      ['backend/learning_tutorial/artifact_generation_v3.py'],
    ),
    artifact_rendering_v3: F(
      'ARP: asset_type code; code block components.',
      'rendered_artifact_objects[]',
      ['backend/learning_tutorial/artifact_rendering_v3.py'],
    ),
    artifact_edm_v3: F(
      'EDM artifact_type CODE; code nodes + metadata.visual_model.',
      'editable_diagram_models[].edm',
      ['backend/learning_tutorial/artifact_edm_v3.py'],
    ),
    artifact_authoring_v3: AUTHORING,
    artifact_studio_v3: STUDIO,
    artifact_layout_engine_v3: LAYOUT,
    artifact_svg_renderer_v3: F(
      'render_profile code_walkthrough → line-by-line code + // comments.',
      'render_entries[] (render_profile: code_walkthrough)',
      [
        'backend/learning_tutorial/rendering/svg/code_content.py',
        'backend/learning_tutorial/rendering/svg/code_renderer.py',
        'backend/learning_tutorial/rendering/edm_enrichment.py',
      ],
    ),
    artifact_rrao_v3: RRAO,
  },
  example: {
    concept_tree_v3: PLANNING_NO_ARTIFACTS,
    knowledge_analysis_v3: PLANNING_SUGGESTED('Example'),
    course_structure_v3: PLANNING_MODULE,
    slide_architecture_v3: PLANNING_SLIDE('Example'),
    curriculum_optimization_v3: PLANNING_OPTIMIZE,
    artifact_specification_v3: SPEC('Example'),
    artifact_generation_v3: F(
      'AKO: scenario, problem, beginner_walkthrough, inputs/outputs.',
      'artifact_knowledge_objects[]',
      ['backend/learning_tutorial/artifact_generation_v3.py'],
    ),
    artifact_rendering_v3: F(
      'ARP: asset_type example; case-study sections as components.',
      'rendered_artifact_objects[]',
      ['backend/learning_tutorial/artifact_rendering_v3.py'],
    ),
    artifact_edm_v3: F(
      'EDM artifact_type CUSTOM; case study section nodes.',
      'editable_diagram_models[].edm',
      ['backend/learning_tutorial/artifact_edm_v3.py'],
    ),
    artifact_authoring_v3: AUTHORING,
    artifact_studio_v3: STUDIO,
    artifact_layout_engine_v3: LAYOUT,
    artifact_svg_renderer_v3: F(
      'render_profile example_case_study → real-world case study sections.',
      'render_entries[] (render_profile: example_case_study)',
      [
        'backend/learning_tutorial/rendering/svg/example_content.py',
        'backend/learning_tutorial/rendering/svg/example_renderer.py',
        'backend/learning_tutorial/rendering/edm_enrichment.py',
      ],
    ),
    artifact_rrao_v3: RRAO,
  },
  flowchart: {
    concept_tree_v3: PLANNING_NO_ARTIFACTS,
    knowledge_analysis_v3: PLANNING_SUGGESTED('Flowchart'),
    course_structure_v3: PLANNING_MODULE,
    slide_architecture_v3: PLANNING_SLIDE('Flowchart'),
    curriculum_optimization_v3: PLANNING_OPTIMIZE,
    artifact_specification_v3: SPEC('Flowchart'),
    artifact_generation_v3: F(
      'AKO: nodes, paths, decision_nodes in visual_model.',
      'artifact_knowledge_objects[]',
      ['backend/learning_tutorial/artifact_generation_v3.py'],
    ),
    artifact_rendering_v3: F(
      'ARP: asset_type flowchart; flow nodes + paths.',
      'rendered_artifact_objects[]',
      ['backend/learning_tutorial/artifact_rendering_v3.py'],
    ),
    artifact_edm_v3: F(
      'EDM artifact_type FLOWCHART.',
      'editable_diagram_models[].edm',
      ['backend/learning_tutorial/artifact_edm_v3.py'],
    ),
    artifact_authoring_v3: AUTHORING,
    artifact_studio_v3: STUDIO,
    artifact_layout_engine_v3: LAYOUT,
    artifact_svg_renderer_v3: F(
      'Default diagram path: flow nodes + paths SVG.',
      'render_entries[]',
      ['backend/learning_tutorial/rendering/svg/renderer.py'],
    ),
    artifact_rrao_v3: RRAO,
  },
  table_comparison: {
    concept_tree_v3: PLANNING_NO_ARTIFACTS,
    knowledge_analysis_v3: PLANNING_SUGGESTED('Table Comparison'),
    course_structure_v3: PLANNING_MODULE,
    slide_architecture_v3: PLANNING_SLIDE('Table Comparison'),
    curriculum_optimization_v3: PLANNING_OPTIMIZE,
    artifact_specification_v3: SPEC('Table Comparison'),
    artifact_generation_v3: F(
      'AKO: rows, columns, comparison_dimensions in visual_model.',
      'artifact_knowledge_objects[]',
      ['backend/learning_tutorial/artifact_generation_v3.py'],
    ),
    artifact_rendering_v3: F(
      'ARP: asset_type table; comparison grid components.',
      'rendered_artifact_objects[]',
      ['backend/learning_tutorial/artifact_rendering_v3.py'],
    ),
    artifact_edm_v3: F(
      'EDM artifact_type TABLE.',
      'editable_diagram_models[].edm',
      ['backend/learning_tutorial/artifact_edm_v3.py'],
    ),
    artifact_authoring_v3: AUTHORING,
    artifact_studio_v3: STUDIO,
    artifact_layout_engine_v3: LAYOUT,
    artifact_svg_renderer_v3: F(
      'render_profile table_trace → comparison table SVG.',
      'render_entries[] (render_profile: table_trace)',
      [
        'backend/learning_tutorial/rendering/svg/table_content.py',
        'backend/learning_tutorial/rendering/svg/table_renderer.py',
      ],
    ),
    artifact_rrao_v3: RRAO,
  },
};

/**
 * Resolve artifact type id from a full artifact_id (e.g. COD_054 → code_example).
 * @param {string | null | undefined} artifactId
 * @returns {string | null}
 */
export function artifactTypeIdFromArtifactId(artifactId) {
  if (!artifactId || typeof artifactId !== 'string') return null;
  const upper = artifactId.toUpperCase();
  const match = ARTIFACT_TYPES.find((t) => upper.startsWith(t.prefix));
  return match?.id ?? null;
}

/**
 * @param {string} artifactTypeId
 * @returns {{ type: typeof ARTIFACT_TYPES[number], cells: Record<string, { summary: string, outputShape: string, backendFiles: string[] }> } | null}
 */
export function getArtifactFlowRow(artifactTypeId) {
  const type = ARTIFACT_TYPES.find((t) => t.id === artifactTypeId);
  if (!type) return null;
  const cells = ARTIFACT_STEP_MATRIX[artifactTypeId];
  if (!cells) return null;
  return { type, cells };
}

export const FLOW3_RENDER_BRANCH = [
  'load EDM from DB (5C.2)',
  '→ enrich_artifact_for_render()',
  '→ ensure_node_sizes_for_labels()',
  '→ if is_code_artifact → code_renderer',
  '→ elif is_table_artifact → table_renderer',
  '→ elif is_example_artifact → example_renderer',
  '→ else → default node/edge diagram SVG',
];
